import { assign, createMachine } from 'xstate';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import { redirectUrlFromQueryParam, isPasswordlessLoginAuthError, type LoginResponseError } from '../../utils/requests/clientAuth/login';
import { browserLogger } from '../../utils/loggers/browser';
import type { SocialButtonErrorParams } from '../../components/SocialButtons/SocialButtons';
import { BannerType } from '../../components/SignIn/BannerWrapper';
import { getParams } from '../../utils/helpers';
type BannerContext = {
  message?: string | React.ReactNode;
  bannerType: BannerType;
};
type SignInContext = {
  banner?: BannerContext;
  reCaptchaTokenV2?: string;
  reCaptchaV2ResetKey: number;
  isDirectSignupClient: boolean;
  isWafEnabled: boolean;
};
type SignInMachineInput = Partial<SignInContext>;
type SignInEvent = {
  type: 'GET_LINK_SUCCESS';
} | {
  type: 'GET_LINK_FAILURE';
  error: unknown;
} | {
  type: 'LOGIN_SUCCESS';
  redirectUrl: string;
} | {
  type: 'PASSKEY_LOGIN_SUCCESS';
  redirectUrl: string;
} | {
  type: 'LOGIN_FAILURE';
  error: LoginResponseError;
  isWafEnabled: boolean;
} | {
  type: 'PASSKEY_LOGIN_FAILURE';
  error: LoginResponseError;
  isWafEnabled: boolean;
} | {
  type: 'SSO_LOGIN_FAILURE';
  error: SocialButtonErrorParams;
  isWafEnabled: boolean;
} | {
  type: 'CLOSE_BANNER';
} | {
  type: 'SET_RECAPTCHA_V2_TOKEN';
  token: string;
} | {
  type: 'REDIRECT_TO_SIGNUP';
} | {
  type: 'EMAIL_PASSWORD_SIGNIN';
} | {
  type: 'PASSWORDLESS_SIGNIN';
} | {
  type: 'DISPLAY_LOGIN_ERROR_BANNER';
  error: BannerContext['bannerType'];
};
export const createSignInMachine = (initial = 'emailPasswordSignIn') => createMachine({
  types: {} as {
    context: SignInContext;
    events: SignInEvent;
    input: SignInMachineInput;
  },
  id: 'signIn',
  initial,
  context: ({
    input
  }) => ({
    banner: undefined,
    reCaptchaTokenV2: undefined,
    reCaptchaV2ResetKey: 0,
    isDirectSignupClient: false,
    isWafEnabled: false,
    ...(input as SignInMachineInput | undefined)
  }),
  on: {
    CLOSE_BANNER: {
      actions: 'closeBanner'
    }
  },
  states: {
    emailPasswordSignIn: {
      initial: 'idle',
      on: {
        LOGIN_SUCCESS: {
          target: 'redirect',
          actions: 'trackLoginAttempt'
        },
        PASSKEY_LOGIN_SUCCESS: {
          target: 'redirect',
          actions: 'trackLoginAttempt'
        },
        REDIRECT_TO_SIGNUP: {
          target: 'redirect'
        },
        SSO_LOGIN_FAILURE: {
          actions: 'logError'
        },
        LOGIN_FAILURE: [{
          guard: 'isReCaptchaV2Error',
          target: '.reCaptchaV2Required',
          actions: 'resetReCaptchaV2AfterLoginFailure'
        }, {
          actions: ['resetReCaptchaV2AfterLoginFailure', 'displayLoginErrorBanner', 'trackLoginAttempt', 'logError']
        }],
        PASSKEY_LOGIN_FAILURE: [{
          guard: 'isReCaptchaV2Error',
          target: '.reCaptchaV2Required',
          actions: 'resetReCaptchaV2AfterLoginFailure'
        }, {
          actions: ['resetReCaptchaV2AfterLoginFailure', 'displayLoginErrorBanner', 'trackLoginAttempt', 'logError']
        }],
        PASSWORDLESS_SIGNIN: {
          target: 'passwordlessSignIn'
        },
        DISPLAY_LOGIN_ERROR_BANNER: {
          actions: 'assignDataToContext'
        }
      },
      states: {
        idle: {},
        reCaptchaV2Required: {
          on: {
            SET_RECAPTCHA_V2_TOKEN: {
              actions: 'assignDataToContext'
            }
          }
        }
      }
    },
    passwordlessSignIn: {
      initial: 'idle',
      on: {
        GET_LINK_SUCCESS: {
          target: '.linkSentSuccess',
          actions: 'trackGetLinkSuccess'
        },
        GET_LINK_FAILURE: [{
          guard: 'isTrackableGetLinkFailure',
          actions: ['displayTrackableGetLinkFailureBanner', 'trackGetLinkFailure']
        }, {
          actions: 'displayUnknownGetLinkFailureBanner'
        }],
        LOGIN_SUCCESS: {
          target: 'redirect',
          actions: 'trackLoginAttempt'
        },
        EMAIL_PASSWORD_SIGNIN: {
          target: 'emailPasswordSignIn'
        }
      },
      states: {
        idle: {
          entry: 'closeBanner'
        },
        linkSentSuccess: {
          entry: 'closeBanner',
          exit: 'closeBanner',
          on: {
            EMAIL_PASSWORD_SIGNIN: {
              target: '#signIn.emailPasswordSignIn'
            },
            GET_LINK_SUCCESS: {
              actions: ['displayResendConfirmationBanner', 'trackGetLinkSuccess']
            },
            GET_LINK_FAILURE: [{
              guard: 'isTrackableGetLinkFailure',
              actions: ['displayTrackableGetLinkFailureBanner', 'trackGetLinkFailure']
            }, {
              actions: 'displayUnknownGetLinkFailureBanner'
            }]
          }
        }
      }
    },
    redirect: {
      type: 'final',
      entry: 'redirect'
    }
  }
}, {
  guards: {
    isReCaptchaV2Error: ({
      event
    }) => (event.type === 'LOGIN_FAILURE' || event.type === 'PASSKEY_LOGIN_FAILURE') && Boolean(event.error.recaptcha),
    isTrackableGetLinkFailure: ({
      event
    }) => {
      if (event.type !== 'GET_LINK_FAILURE' || !isPasswordlessLoginAuthError(event.error)) {
        return false;
      }
      const {
        code: errorCode
      } = event.error.response.data.error;
      return errorCode === 'email_not_found' || errorCode === 'client_inactive';
    }
  },
  actions: {
    resetReCaptchaV2AfterLoginFailure: assign({
      reCaptchaTokenV2: () => undefined,
      reCaptchaV2ResetKey: ({
        context
      }) => context.reCaptchaV2ResetKey + 1
    }),
    assignDataToContext: assign(({
      context,
      event
    }) => {
      if (event.type === 'SET_RECAPTCHA_V2_TOKEN') {
        return {
          ...context,
          reCaptchaTokenV2: event.token
        };
      }
      if (event.type === 'DISPLAY_LOGIN_ERROR_BANNER') {
        return {
          ...context,
          banner: {
            bannerType: event.error
          }
        };
      }
      return context;
    }),
    closeBanner: assign({
      banner: () => undefined
    }),
    displayLoginErrorBanner: assign({
      banner: ({
        event
      }) => event.type === 'LOGIN_FAILURE' || event.type === 'PASSKEY_LOGIN_FAILURE' ? {
        bannerType: 'error',
        message: event.error.error_message
      } : undefined
    }),
    displayResendConfirmationBanner: assign({
      banner: () => ({
        bannerType: 'resend_confirmation'
      })
    }),
    displayTrackableGetLinkFailureBanner: assign({
      banner: ({
        event
      }) => {
        if (event.type !== 'GET_LINK_FAILURE' || !isPasswordlessLoginAuthError(event.error)) {
          return undefined;
        }
        const errorCode = event.error.response.data.error.code;
        if (errorCode !== 'email_not_found' && errorCode !== 'client_inactive') {
          return undefined;
        }
        return {
          bannerType: errorCode
        };
      }
    }),
    displayUnknownGetLinkFailureBanner: assign({
      banner: () => ({
        bannerType: 'unknown_error'
      })
    }),
    redirect: ({
      context,
      event
    }) => {
      if (event.type === 'LOGIN_SUCCESS' || event.type === 'PASSKEY_LOGIN_SUCCESS') {
        const redirect = redirectUrlFromQueryParam() || event.redirectUrl;
        const nextRedirect = redirect?.includes('/login') ? '/home' : redirect;
        window.location.assign(nextRedirect ?? '/home');
      }
      if (event.type === 'REDIRECT_TO_SIGNUP') {
        const params = getParams();
        const orderReviewParams = encodeURIComponent(params?.get('order_review_params') ?? '');
        const businessLine = encodeURIComponent(params?.get('business_line') ?? '');
        window.location.assign(
        // When the user came from a specialized signup form, return them there.
        context.isDirectSignupClient ? `/product/signup?business_line=${businessLine}&order_review_params=${orderReviewParams}` : '/signup');
      }
    },
    trackGetLinkSuccess: () => {
      recordMark('passwordless_signin_request', {
        success: true
      });
    },
    trackGetLinkFailure: ({
      event
    }) => {
      if (event.type !== 'GET_LINK_FAILURE' || !isPasswordlessLoginAuthError(event.error)) {
        return;
      }
      const errorReasonMapping: Record<string, string> = {
        email_not_found: 'account_not_found',
        client_inactive: 'account_inactive'
      };
      const {
        code: errorCode
      } = event.error.response.data.error;
      if (errorCode !== 'email_not_found' && errorCode !== 'client_inactive') {
        return;
      }
      recordMark('passwordless_signin_request', {
        success: false,
        reason: errorReasonMapping[errorCode]
      });
    },
    trackLoginAttempt: ({
      event
    }) => {
      if (event.type === 'PASSKEY_LOGIN_SUCCESS') {
        browserLogger({
          level: 'info',
          team: 'client-identity-access-management',
          feature: 'passkeys',
          message: 'attempting to fire sign-in-attempt event for passkey login'
        });
      }
      fireEvent({
        eventName: 'sign-in-attempt',
        eventAttributes: {
          signInType: 'email',
          signInSuccess: event.type === 'LOGIN_SUCCESS' || event.type === 'PASSKEY_LOGIN_SUCCESS'
        }
      });
      if (event.type === 'PASSKEY_LOGIN_SUCCESS') {
        browserLogger({
          level: 'info',
          team: 'client-identity-access-management',
          feature: 'passkeys',
          message: 'completed firing of sign-in-attempt event for passkey login'
        });
      }
    },
    logError: ({
      event
    }) => {
      if (event.type === 'SSO_LOGIN_FAILURE' || event.type === 'LOGIN_FAILURE') {
        browserLogger({
          level: 'info',
          team: 'client-onboarding',
          feature: 'email-login',
          message: 'Sign In error',
          context: {
            error: event.error,
            isWafEnabled: event.isWafEnabled
          }
        });
      } else if (event.type === 'PASSKEY_LOGIN_FAILURE') {
        browserLogger({
          level: 'info',
          team: 'client-identity-access-management',
          feature: 'passkeys',
          message: 'Passkey Sign In error',
          context: {
            error: event.error,
            isWafEnabled: event.isWafEnabled
          }
        });
      }
    }
  }
});
export const signInMachine = createSignInMachine('emailPasswordSignIn');